import { useState, useCallback } from 'react';
import { window } from '../util/globals';

const STORAGE_KEY_PREFIX = '@@lifeomic/store/';

export const getPrefixedStorageKey = (itemKey: string) =>
  STORAGE_KEY_PREFIX + itemKey;

const readPersistedValue = <TValue>(
  storage: Storage,
  storageKey: string,
): TValue | null => {
  try {
    const storedValue = storage.getItem(getPrefixedStorageKey(storageKey));

    if (!storedValue) return null;

    return (JSON.parse(storedValue) as TValue) || null;
  } catch (e) {
    console.error(
      'Error occurred retrieving the persisted storage item with key',
      storageKey,
      e,
    );
    return null;
  }
};

const writePersistedValue = <TValue>(
  storage: Storage,
  storageKey: string,
  value: TValue,
) => {
  try {
    const storedValue = JSON.stringify(value);
    storage.setItem(getPrefixedStorageKey(storageKey), storedValue);
  } catch (e) {
    console.error(
      'Error occurred trying to persist the storage item with key',
      storageKey,
      e,
    );
  }
};

export const safeGetPersistedValue = <TValue>(
  storageKey: string,
): TValue | null => readPersistedValue<TValue>(window.localStorage, storageKey);

export const safePersistValue = <TValue>(storageKey: string, value: TValue) =>
  writePersistedValue(window.localStorage, storageKey, value);

/**
 * `useState` whose value initializes from, and persists every update to, the
 * given Web Storage area.
 */
export const useStorageState = <TValue = any>(
  getStorage: () => Storage,
  storageKey: string,
  defaultValue: TValue,
): [TValue, (updatedState: TValue) => void] => {
  const [state, setState] = useState(() => {
    const persisted = readPersistedValue<TValue>(getStorage(), storageKey);
    return persisted === null ? defaultValue : persisted;
  });

  const interceptSetState = useCallback(
    (updatedState: TValue) => {
      writePersistedValue(getStorage(), storageKey, updatedState);
      setState(updatedState);
    },
    [getStorage, setState, storageKey],
  );

  return [state, interceptSetState];
};

const getLocalStorage = () => window.localStorage;

/**
 * A hook with an identical interface to the React useState except the state value attempts to initialize from
 * local storage.  Every state update is persisted to local to load.
 *
 * It's important to not rely on the local storage value for application-critical data or sensitive information.
 * Data can be cleared and can be accessible from anyone using the browser.
 * @param storageKey - A unique identifier to associate to a local storage value
 * @param defaultValue - When no value is found in local storage, this is used
 */
const useLocalStorageState = <TValue = any>(
  storageKey: string,
  defaultValue: TValue,
): [TValue, (updatedState: TValue) => void] =>
  useStorageState(getLocalStorage, storageKey, defaultValue);

export default useLocalStorageState;
